import React from 'react';
import { useQueryClient } from 'react-query';
import { Alert } from '../../../../../../CommonComponents/OPL/Alert/Alert';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../../CommonComponents/OPL/Button/Button';
import { Modal } from '../../../../../../CommonComponents/OPL/Modal/Modal';
import { ModalBody } from '../../../../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalCloseButton } from '../../../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalFooter } from '../../../../../../CommonComponents/OPL/Modal/ModalFooter';
import { ModalHeader } from '../../../../../../CommonComponents/OPL/Modal/ModalHeader';
import { ACCOUNTS_QUERY_KEY } from '../../../../AccountsConstants';
import { ACCOUNT_PREFERENCES_QUERY_KEY } from '../../../../api/useAccountsQuery';
import { useRemoveLinkedInstitution } from '../../../../api/useRemoveLinkedInstitution';
import { Institution, useManageLinks } from '../../useManageLinks';

interface Props {
  institution: Institution | null;
  onClose: () => void;
  clientId: string;
}

export const DeleteModal: React.FC<Props> = ({ institution, clientId, onClose }) => {
  const queryClient = useQueryClient();
  const { refetch } = useManageLinks();

  const {
    mutateAsync: removeInstitutionConnection,
    isLoading: removeInstitutionConnectionIsLoading,
    error: removeInstitutionConnectionError,
  } = useRemoveLinkedInstitution();

  if (!institution) {
    return null;
  }

  return (
    <Modal isOpen size="medium" onRequestClose={onClose}>
      <ModalHeader variant="error">Warning</ModalHeader>

      <ModalCloseButton disabled={removeInstitutionConnectionIsLoading} onClick={onClose} />

      {removeInstitutionConnectionError && (
        <Alert
          message={removeInstitutionConnectionError.message}
          variant="error"
          mx="24"
          mt="0"
          mb="16"
        />
      )}

      <ModalBody>
        Are you sure you want to unlink your <strong>{institution.InstitutionName}</strong>{' '}
        accounts? Any associated accounts will also be deleted.
      </ModalBody>

      <ModalFooter>
        <Box display="flex" justifyContent="flex-end">
          <Button
            disabled={removeInstitutionConnectionIsLoading}
            variant="subtleInactive"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            disabled={removeInstitutionConnectionIsLoading}
            isLoading={removeInstitutionConnectionIsLoading}
            variant="error"
            ml="12"
            onClick={async () => {
              await removeInstitutionConnection({
                clientId,
                externalAccountId: institution.ExternalLinkId,
              });
              refetch();
              queryClient.invalidateQueries([ACCOUNTS_QUERY_KEY, ACCOUNT_PREFERENCES_QUERY_KEY]);
              onClose();
            }}
          >
            Remove Accounts
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
};
